import { motion } from 'framer-motion'
import PropTypes from 'prop-types'
import { useState, useRef, useMemo, useEffect } from 'react'

import BusyOverlay from 'components/common/BusyOverlay'
import TextButton from 'components/common/TextButton'
import Icon from 'components/Icon'
import useAgent from 'hooks/use-agent'
import useAuth from 'hooks/use-auth'
import { FIELD_CARDS_CONTENT } from 'hooks/use-onboarding-data'
import theme from 'theme'

import GuidedCard, { CARD_HEIGHT, CARD_WIDTH } from '../cards/GuideCard'

// ToDo: Move this into common and use it for scenarios runs as well
function TabSlider({ tabsArray, customNavigationStyle }) {
  // tabslist refs
  const [isTabNavRightVisible, setIsTabNavRightVisible] = useState(false)
  const [isTabNavLeftVisible, setIsTabNavLeftVisible] = useState(false)
  const [tabScroll, setTabScroll] = useState(0)
  const tabsRef = useRef()

  const tabsListRef = useRef()
  // WIDTH + BORDER
  const tabItemWidth = CARD_WIDTH + 2

  const [tabsListScrollWidth, tabsListWidth] = useMemo(() => {
    return [
      tabsListRef.current?.scrollWidth ?? 0,
      tabsListRef.current?.getBoundingClientRect().width ?? 0
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabsListRef.current, tabsArray])

  // Choose to enable or disable either arrows
  // Left arrow is enabled if there was previously a scroll that happened
  // right arrow is enabled if the total width of the tabs list is greater than what we don't see
  // on the screen (how much we have scrolled) + what we see on the screen
  useEffect(() => {
    if (tabScroll > 0) {
      setIsTabNavLeftVisible(true)
    } else {
      setIsTabNavLeftVisible(false)
    }

    if (tabsListScrollWidth > tabScroll + tabsListWidth) {
      setIsTabNavRightVisible(true)
    } else {
      setIsTabNavRightVisible(false)
    }
  }, [tabScroll, tabsListRef, tabsListScrollWidth, tabsListWidth])

  // Calculate the scroll length
  // === the previous scroll +/- the width of the largest numbers of tabs items
  // that can be seen on screen
  //(found by dividing the client width[ rid of the padding] by
  // the width of a single item + 1px error margin times the two margins left and right of it )
  // then multiplying by the size of an item added to its margins

  const scrollTabsRight = () => {
    const wantedScroll =
      tabScroll +
      (Math.floor(tabsListWidth / (tabItemWidth + 1 + 2 * theme.space[3])) -
        1) *
        (tabItemWidth + 1 + 2 * theme.space[3])

    if (tabsListScrollWidth < wantedScroll + tabsListWidth) {
      // this is the last slide we are about to do
      // so only set it to the exact client view width
      // 48 = 24 x 2 the paddings
      setTabScroll(
        tabScroll + (tabsListScrollWidth - (tabScroll + tabsListWidth - 8))
      )
    } else {
      setTabScroll(wantedScroll)
    }
  }

  const scrollTabsLeft = () => {
    const wantedScroll =
      tabScroll -
      (Math.floor(tabsListWidth / (tabItemWidth + 1 + 2 * theme.space[4])) -
        1) *
        (tabItemWidth + 1 + 2 * theme.space[4])
    if (wantedScroll < 0) {
      setTabScroll(0)
    } else {
      setTabScroll(wantedScroll)
    }
  }

  // Common css style for both navigation arrows
  const tabNavigationStyle = (isNavigationEnabled) => ({
    height: tabItemWidth + 11,
    padding: `0 ${theme.space[3]}px`,
    borderBottom: theme.border.base,
    marginTop: -10,
    backgroundColor: isNavigationEnabled
      ? theme.colors.white[0]
      : 'transparent',
    display: 'grid',
    gridTemplateColumns: '1fr',
    alignItems: 'center',
    cursor: isNavigationEnabled ? 'pointer' : 'initial'
  })

  return (
    <div
      css={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 100%)',
        borderTop: theme.border.base,
        [theme.mq[0]]: {
          borderTop: 0,
          marginLeft: '-1px'
        }
      }}
    >
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: `${theme.space[6]}px minmax(0, 100%) ${theme.space[6]}px`,
          marginLeft: -theme.space[6] // push back the grid to align with borders
        }}
      >
        <TextButton
          aria-label="View more tiles"
          css={{
            borderRight: isTabNavLeftVisible ? theme.border.base : 'none',
            zIndex: 1,
            ...(customNavigationStyle
              ? customNavigationStyle({
                  isLeftNavigation: true,
                  displayNav: isTabNavLeftVisible,
                  tabItemWidth
                })
              : tabNavigationStyle(isTabNavLeftVisible)),
            width: 44,
            padding: theme.space[3],
            visibility: isTabNavLeftVisible ? 'visible' : 'hidden',
            marginLeft: 40 // push the arrow up to overlay the slider
          }}
          data-testid="navigate-left"
          onClick={isTabNavLeftVisible ? scrollTabsLeft : undefined}
        >
          {isTabNavLeftVisible && (
            <Icon
              css={{
                color: theme.colors.steel[2],
                marginLeft: -theme.space[1]
              }}
              icon="chevronLeft"
            />
          )}
        </TextButton>

        <div
          ref={tabsRef}
          style={{
            overflow: 'hidden'
          }}
        >
          <div ref={tabsListRef}>
            <motion.div
              animate={{ x: -tabScroll }}
              css={{
                paddingRight: theme.space[5],
                textDecoration: 'none',
                overflow: 'visible',
                transition: 'transform 250ms cubic-bezier(0, 0, 0.2, 1) 0s',
                borderBottom: 'none',
                paddingLeft: 0,
                display: 'grid',
                gridTemplateColumns: `repeat(${tabsArray.length}, ${CARD_WIDTH}px)`,
                gap: theme.space[3]
              }}
              transition={{
                type: 'spring',
                stiffness: 550,
                damping: 50
              }}
            >
              {tabsArray}
            </motion.div>
          </div>
        </div>

        <TextButton
          aria-label="View older tiles"
          css={{
            borderLeft: isTabNavRightVisible ? theme.border.base : 'none',
            ...(customNavigationStyle
              ? customNavigationStyle({
                  displayTabNavRight: isTabNavRightVisible,
                  tabItemWidth,
                  displayNav: isTabNavRightVisible
                })
              : tabNavigationStyle(isTabNavRightVisible))
          }}
          data-testid="navigate-right"
          onClick={isTabNavRightVisible ? scrollTabsRight : undefined}
        >
          {isTabNavRightVisible && (
            <Icon
              css={{
                color: theme.colors.steel[2]
              }}
              icon="chevronRight"
            />
          )}
        </TextButton>
      </div>
    </div>
  )
}

TabSlider.propTypes = {
  tabsArray: PropTypes.array,
  panelsArray: PropTypes.array,
  customNavigationStyle: PropTypes.func,
  tabsListStyle: PropTypes.object
}

export default function HeaderSlider() {
  const { currentTeamId } = useAuth()

  const { data: responses, isLoading } = useAgent(
    `/teams/${currentTeamId}/profile`,
    {}
  )

  const { cloudProvider, containers, incidents, logging, monitoring, paging } =
    responses

  const cardNameToDisplay = Object.fromEntries(
    []
      .concat(cloudProvider, containers, incidents, logging, monitoring, paging)
      .map((key) => (key ? [key, true] : []))
  )

  const newUserCardDisplay = useMemo(() => {
    const fieldCards = Object.values(FIELD_CARDS_CONTENT).flat()
    if (
      Object.keys(cardNameToDisplay).filter((name) => name !== 'undefined')
        .length === 0
    ) {
      const cards = fieldCards.filter((card) => card.name === 'common')
      cards.push({
        name: 'common',
        badgeLabel: 'Getting started',
        title: 'Chaos Engineering Monitoring & Metrics Guide',
        buttonLabel: 'Read installation Guide',
        href: 'https://www.gremlin.com/community/tutorials/chaos-engineering-monitoring-metrics-guide/'
      })
      return cards
    }
    return fieldCards
  }, [cardNameToDisplay])

  if (isLoading) return <BusyOverlay rounded shown />

  const tabsArray = newUserCardDisplay.map((card, index) => {
    if (card.name === 'common' || cardNameToDisplay[card.name]) {
      return (
        <div
          key={`header-slider-card-${index}`}
          css={{
            borderBottomWidth: 3,
            '&:not(:first-of-type)': {
              marginLeft: theme.space[4]
            }
          }}
        >
          <GuidedCard
            badgeLabel={card.badgeLabel}
            buttonLabel={card.buttonLabel}
            cardIcon={card.icon ? card.icon() : null}
            css={{
              background: theme.colors.white[0]
            }}
            href={card.href}
            labelColor={card.labelColor}
            to={card.to}
          >
            {card.title}
          </GuidedCard>
        </div>
      )
    }
    return null
  })

  return (
    <TabSlider
      customNavigationStyle={({
        isNavigationEnabled,
        isLeftNavigation,
        displayNav
      }) => ({
        height: CARD_HEIGHT,
        padding: `0 ${isLeftNavigation ? 0 : theme.space[3]}px`,
        backgroundColor: theme.colors.panel[0],
        boxShadow: displayNav
          ? isLeftNavigation
            ? '5px 0px 5px -5px rgba(0,0,0,0.16)'
            : '-5px 0px 5px -5px rgba(0,0,0,0.16)'
          : 'none',
        display: 'grid',
        gridTemplateColumns: '1fr',
        alignItems: 'center',
        cursor: isNavigationEnabled ? 'pointer' : 'initial',
        zIndex: 1,
        '&:hover': {
          backgroundColor: theme.colors.panel[0]
        }
      })}
      tabsArray={tabsArray}
      tabsListStyle={{
        paddingLeft: 0
      }}
    />
  )
}
